import { Text, Grid, Theme } from '@simplywallst/ui-core';

import {
  FeedCardLink,
  FeedCardItem,
  FeedCardItemSkeleton,
  NarrativeFeedCard,
} from '@/components/FeedCard';

import useCompanyFeed from '@/hooks/useCompanyFeed';
import type { Options } from '@/hooks/useCompanyFeed';

import { EmptyState } from './styled';
import type { EventCategory } from '@/constants/events';
import { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

export interface Props {
  lookup: string | null;
  fetchLimit?: Options['limit'];
  showLimit?: number;
  isCompanyReport?: boolean;
  types?: Options['types'];
  filterByEventCategory?: EventCategory;
  hoverBackground?: keyof Theme['color'];
}

export const CompanyFeed = ({
  lookup,
  fetchLimit,
  showLimit,
  isCompanyReport,
  types,
  filterByEventCategory,
  hoverBackground,
}: Props) => {
  const { data, isLoading } = useCompanyFeed(lookup, {
    limit: fetchLimit,
    types,
  });

  const items = data?.feedItems;
  const filteredItems = useMemo(() => {
    if (!filterByEventCategory || !items) return items;
    return items.filter(
      (item) => item.categories?.includes(filterByEventCategory)
    );
  }, [items, filterByEventCategory]);

  const t = useTranslation();

  if (isLoading || lookup === null) {
    return (
      <Grid rowGap="16px">
        <FeedCardItemSkeleton />
        <FeedCardItemSkeleton />
        <FeedCardItemSkeleton />
      </Grid>
    );
  }

  if (!data || !filteredItems?.length) {
    return (
      <EmptyState>
        <Text typography="xSmall" color="whiteRGBA" opacity={0.3}>
          {t('page.companyReport.feed.empty')}
        </Text>
      </EmptyState>
    );
  }

  return (
    <>
      {filteredItems.slice(0, showLimit).map((feedItem, i) => {
        if (feedItem.type === 'narrative') {
          return (
            <NarrativeFeedCard
              company={data}
              feedItem={feedItem}
              index={i}
              key={`${i}-${feedItem.date}-${feedItem.url}`}
            />
          );
        } else {
          return (
            <FeedCardLink
              index={i}
              key={`${i}-${feedItem.date}-${feedItem.url}`}
              company={data}
              feedItem={feedItem}
              lookUp={lookup}
              isCompanyReport={isCompanyReport}
            >
              <FeedCardItem
                feedItem={feedItem}
                hoverBackground={hoverBackground}
              />
            </FeedCardLink>
          );
        }
      })}
    </>
  );
};
